import MenuIcon from '@mui/icons-material/Menu';
import { IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * @typedef {Object} MobileMenuButtonProps
 * @property {() => void} onClick - Click handler for opening mobile menu
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [sx] - Additional Material-UI sx props
 * @property {string} [ariaLabel] - Accessibility label for the button
 */

/**
 * Mobile Menu Button component
 *
 * A reusable mobile menu toggle button with consistent styling and
 * accessibility features for the EllaFin header.
 * All colors are sourced from the MUI theme for theme switching support.
 *
 * @param {MobileMenuButtonProps} props - Component props
 * @returns {JSX.Element} Mobile menu button
 */
function MobileMenuButton({
  onClick,
  className,
  sx = {},
  ariaLabel = 'open navigation menu',
}) {
  const theme = useTheme();

  return (
    <IconButton
      color="inherit"
      aria-label={ariaLabel}
      edge="start"
      onClick={onClick}
      className={className}
      sx={{
        ml: 'auto',
        color: 'inherit',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          color: theme.palette.secondary.main,
          backgroundColor: 'transparent',
          transform: 'scale(1.05)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
        ...sx,
      }}
    >
      <MenuIcon />
    </IconButton>
  );
}

export default MobileMenuButton;
